/**
 * Magic-link entry: /c/:passcode
 *
 * Reads the passcode from the URL, joins via the existing classroom API,
 * and forwards into /classroom/:lessonId. If the lesson hasn't opened yet
 * (10 min before start) we show a friendly waiting screen.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";

export default function MagicLink() {
  const { passcode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ phase: "loading", message: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!passcode) {
        setStatus({ phase: "error", message: "Missing passcode in link." });
        return;
      }
      try {
        const data = await api.join(passcode);
        if (cancelled) return;
        if (!data.can_join_now) {
          setStatus({
            phase: "waiting",
            message: data.join_window_message || "Lesson hasn't opened yet.",
            data,
          });
          return;
        }
        navigate(`/classroom/${data.lesson_id}`, {
          replace: true,
          state: { ...data, passcode },
        });
      } catch (err) {
        if (cancelled) return;
        setStatus({
          phase: "error",
          message: err.message || "Could not open the classroom.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [passcode, navigate]);

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="magic-link-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Classroom
        </div>

        {status.phase === "loading" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Opening your classroom…
            </h1>
            <p className="mt-4 text-base text-[#1C1C1C]/70">One moment please.</p>
          </>
        )}

        {status.phase === "waiting" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Almost there.
            </h1>
            <p
              className="mt-4 text-base text-[#1C1C1C]/70"
              data-testid="magic-link-waiting"
            >
              {status.message}
            </p>
            {status.data?.title && (
              <div className="mt-8 p-5 bg-white rounded-xl border border-[#1C1C1C]/10">
                <div className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
                  Lesson
                </div>
                <div className="font-display text-xl text-[#1C1C1C]">
                  {status.data.title}
                </div>
              </div>
            )}
          </>
        )}

        {status.phase === "error" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Hmm.
            </h1>
            <p
              className="mt-4 text-base text-[#B83A3A]"
              data-testid="magic-link-error"
            >
              {status.message}
            </p>
            <p className="mt-6 text-sm text-[#1C1C1C]/60">
              Double-check the link from your email — or try entering your
              passcode manually on the{" "}
              <a href="/students" className="underline underline-offset-4">
                Students
              </a>{" "}
              or{" "}
              <a href="/teachers" className="underline underline-offset-4">
                Teachers
              </a>{" "}
              page.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
